import { useEffect, useMemo } from 'react'
import {
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { getBankCards } from 'services/bankManagements'
import { createWithdrawalRequest, getMyDeposits } from 'services/deposits'
import { toman } from 'utilities/helper/format'
import WizardChrome, {
    IReceiptLine,
} from 'pages/loans/manager/grant/components/WizardChrome'
import StickyAction from 'pages/loans/manager/grant/components/StickyAction'
import { PulseCard, SectionError } from 'components/states'
import useWithdrawalDraft from './useWithdrawalDraft'
import StepFund from './steps/StepFund'
import StepAmount from './steps/StepAmount'
import StepTerms from './steps/StepTerms'
import StepReview from './steps/StepReview'

const TOTAL_STEPS = 4
const MIN_AMOUNT = 1_000

const STEP_TITLES: Record<number, { title: string; subtitle?: string }> = {
    1: {
        title: 'از کدام صندوق برداشت می‌کنید؟',
        subtitle:
            'فقط صندوق‌هایی که در آن‌ها سرمایه دارید. عضویتتان در صندوق دست‌نخورده می‌ماند.',
    },
    2: { title: 'چه مبلغی لازم دارید؟' },
    3: { title: 'چطور دریافت کنید؟' },
    4: { title: 'توضیح و ثبت درخواست' },
}

/**
 * درخواست برداشت, as a wizard.
 *
 * What this replaces is not a worse screen — it is the absence of one. The
 * only way to get money out of a fund was «درخواست تسویه و خروج», which is
 * all-or-nothing by construction: a member who needed 1,000,000 of the
 * 10,000,000 they held had to ask to leave to get it. The feature is the
 * partial ask; the wizard is so that four small questions never arrive as one
 * intimidating form about the member's own money.
 *
 * Step lives in `?step=` rather than state alone so the phone's back button
 * walks the wizard instead of abandoning it, and answered steps collapse into
 * tappable receipt lines — same contract as the اعطای وام wizard, whose chrome
 * this reuses outright.
 */
export default function WithdrawRequestWizard() {
    const params = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [searchParams, setSearchParams] = useSearchParams()

    const step = Math.min(
        Math.max(Number(searchParams.get('step') || 1), 1),
        TOTAL_STEPS
    )

    const { draft, patch, clear } = useWithdrawalDraft()
    const {
        data: deposits,
        isLoading,
        isError,
        refetch,
    } = useQuery(['my-deposits'], getMyDeposits, { staleTime: 60_000 })
    // same key as StepTerms so react-query serves one request for both
    const { data: cardsData } = useQuery(['bank-cards'], () => getBankCards())

    // a fund in the route wins over the draft: arriving from a specific fund's
    // page means the question is already answered
    const routeFundId = params?.id ? Number(params.id) : null
    const fundId = routeFundId ?? draft.fundId

    const funds = deposits?.funds ?? []
    const fund = funds.find((item) => item.moneypool_id === fundId)

    useEffect(() => {
        posthog.capture('withdrawal_request_step_viewed', {
            fund_id: fundId,
            step,
        })
    }, [step, fundId])

    const goTo = (next: number) => setSearchParams({ step: String(next) })
    // history.back, so the arrow and the phone's own back button agree
    const back = () => navigate(-1)

    // a deep link to a later step with no fund chosen is a dead end
    useEffect(() => {
        if (!isLoading && step > 1 && !fund) {
            setSearchParams({ step: '1' }, { replace: true })
        }
    }, [step, fund, isLoading, setSearchParams])

    const amount = Number(draft.amount || 0)
    const capital = fund?.capital ?? 0
    const debt = fund?.debt ?? 0

    const { mutate: submit, isLoading: isSubmitting } = useMutation({
        mutationFn: () =>
            createWithdrawalRequest({
                moneypoolId: fundId as number,
                amount,
                partial_accepted: draft.partialAccepted,
                user_description: draft.note,
                selected_card_number: draft.cardId,
            }),
        onSuccess: (created) => {
            posthog.capture('withdrawal_request_submitted', {
                fund_id: fundId,
                amount,
                partial_accepted: draft.partialAccepted,
                has_note: Boolean(draft.note.trim()),
            })
            clear()
            queryClient.invalidateQueries(['my-deposits'])
            toast.success('درخواست برداشت ثبت شد و به مدیران اطلاع داده شد')
            navigate(
                `/fund/withdraw-request/detail/${created.id}?fund=${fundId}`,
                { replace: true }
            )
        },
        onError: (error: any) => {
            posthog.capture('withdrawal_request_failed', { fund_id: fundId })
            const reason = error?.response?.data?.params
            toast.error(reason || 'ثبت درخواست انجام نشد')
        },
    })

    const receipt = useMemo<IReceiptLine[]>(() => {
        const lines: IReceiptLine[] = []
        if (step > 1 && fund) {
            lines.push({
                label: 'صندوق',
                value: fund.moneypool_name,
                step: 1,
            })
        }
        if (step > 2 && amount > 0) {
            lines.push({ label: 'مبلغ', value: toman(amount), step: 2 })
        }
        if (step > 3) {
            lines.push({
                label: 'پرداخت بخشی',
                value: draft.partialAccepted ? 'قابل قبول' : 'قابل قبول نیست',
                step: 3,
            })
        }
        return lines
    }, [step, fund, amount, draft.partialAccepted])

    const canContinue = () => {
        if (step === 2) return amount >= MIN_AMOUNT && amount <= capital
        return true
    }

    const onContinue = () => {
        if (!canContinue()) {
            if (amount > capital) {
                toast.error('مبلغ از سرمایهٔ شما در این صندوق بیشتر است')
            } else {
                toast.error('مبلغ درخواست را وارد کنید')
            }
            return
        }
        goTo(step + 1)
    }

    const selectedCard = (cardsData?.results ?? []).find(
        (card) => card.id === draft.cardId
    )
    const { title, subtitle } = STEP_TITLES[step]

    return (
        <WizardChrome
            flowTitle="درخواست برداشت"
            title={title}
            subtitle={subtitle}
            step={step}
            totalSteps={TOTAL_STEPS}
            receipt={receipt}
            onBack={back}
            onJumpTo={goTo}
        >
            {isLoading && <PulseCard />}
            {isError && <SectionError onRetry={() => refetch()} />}

            {!isLoading && step === 1 && (
                <StepFund
                    funds={funds}
                    selectedId={fundId}
                    onSelect={(picked) => {
                        patch({ fundId: picked.moneypool_id })
                        goTo(2)
                    }}
                />
            )}
            {!isLoading && step === 2 && fund && (
                <StepAmount
                    capital={capital}
                    debt={debt}
                    amount={draft.amount}
                    onChange={(value) => patch({ amount: value })}
                />
            )}
            {!isLoading && step === 3 && fund && (
                <StepTerms
                    amount={amount}
                    partialAccepted={draft.partialAccepted}
                    cardId={draft.cardId}
                    onChange={patch}
                />
            )}
            {!isLoading && step === 4 && fund && (
                <StepReview
                    fundName={fund.moneypool_name}
                    amount={amount}
                    capital={capital}
                    debt={debt}
                    partialAccepted={draft.partialAccepted}
                    cardLabel={
                        selectedCard
                            ? `${selectedCard.bank_name} — ${selectedCard.card_number}`
                            : null
                    }
                    note={draft.note}
                    onNoteChange={(value) => patch({ note: value })}
                />
            )}

            {/* step 1 advances by picking a fund, so it gets no «ادامه» bar */}
            {step > 1 && step < TOTAL_STEPS && (
                <StickyAction>
                    <button
                        type="button"
                        onClick={onContinue}
                        className=" w-full rounded-full bg-green-1 py-3 text-[15px] font-bold text-white transition-transform active:scale-[0.99] motion-reduce:transition-none"
                    >
                        ادامه
                    </button>
                </StickyAction>
            )}

            {step === TOTAL_STEPS && fund && (
                <StickyAction
                    summary={
                        <p className=" rounded-xl bg-brand-soft px-3 py-2 text-[11px] font-bold text-brand-deep">
                            درخواست {toman(amount)} از {fund.moneypool_name}
                        </p>
                    }
                >
                    <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => submit()}
                        className=" w-full rounded-full bg-green-1 py-3 text-[15px] font-bold text-white disabled:opacity-60"
                    >
                        {isSubmitting ? 'در حال ثبت…' : 'ثبت درخواست'}
                    </button>
                </StickyAction>
            )}
        </WizardChrome>
    )
}
